import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaApple, FaGooglePlay, FaPlay, FaStar, FaCheck, FaShieldAlt, FaLock, FaClock, FaHeadset } from 'react-icons/fa';
import { getAppStoreURL, APP_STORE_URL, PLAY_STORE_URL } from '../utils/deviceDetection';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" data-aos="fade-up">
        <div className="container">
          <div className="hero-content">
            <div className="ai-badge" data-aos="zoom-in">
              <span className="badge-pulse"></span>
              {t('aiWatchman.badge')}
            </div>
            <h1 className="hero-title">
              {t('hero.title')} <span className="gradient-text">{t('hero.titleHighlight')}</span>
            </h1>
            <h2 className="hero-subtitle">{t('hero.subtitle')}</h2>
            <p className="hero-description">{t('hero.description')}</p>
            
            <div className="hero-buttons">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                <FaApple />
                App Store
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
                <FaGooglePlay />
                Google Play
              </a>
              {/* <button className="btn btn-outline btn-lg">
                <FaPlay />
                {t('hero.watchDemo')}
              </button> */}
            </div>

            <p className="hero-note">{t('cta.noCreditCard')}</p>
          </div>

          <div className="hero-image" data-aos="fade-left" data-aos-delay="200">
            <div className="phone-mockup">
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="app-preview">
                    <div className="preview-header">
                      <img src="/main_logo.png" alt="Land Tracker" className="preview-logo" />
                      <div className="preview-title">Land Tracker</div>
                    </div>
                    <div className="preview-content">
                      <div className="preview-card featured-preview">
                        <div className="card-icon">🤖</div>
                        <div className="card-title">AI-Watchman</div>
                        <div className="card-badge">AI-POWERED</div>
                      </div>
                      <div className="preview-card featured-preview">
                        <div className="card-icon">📐</div>
                        <div className="card-title">Boundary Capture</div>
                        <div className="card-badge">GPS-PRECISE</div>
                      </div>
                      <div className="preview-card">
                        <div className="card-icon">📍</div>
                        <div className="card-title">Property Management</div>
                      </div>
                      <div className="preview-card">
                        <div className="card-icon">🗺️</div>
                        <div className="card-title">Interactive Maps</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats" data-aos="fade-up">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <h3 className="stat-number gradient-text">10K+</h3>
              <p className="stat-label">{t('stats.users')}</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-number gradient-text">50K+</h3>
              <p className="stat-label">{t('stats.properties')}</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-number gradient-text">200K+</h3>
              <p className="stat-label">{t('stats.documents')}</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-number gradient-text">98%</h3>
              <p className="stat-label">{t('stats.satisfaction')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>{t('features.title')}</h2>
            <p>{t('features.subtitle')}</p>
          </div>

          <div className="features-grid">
            <div className="feature-card" data-aos="fade-up" data-aos-delay="0">
              <div className="feature-icon">📍</div>
              <h3>{t('features.property.title')}</h3>
              <p>{t('features.property.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="50">
              <div className="feature-icon">🤖</div>
              <h3>{t('features.watchman.title')}</h3>
              <p>{t('features.watchman.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="100">
              <div className="feature-icon">🗺️</div>
              <h3>{t('features.map.title')}</h3>
              <p>{t('features.map.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="150">
              <div className="feature-icon">📐</div>
              <h3>{t('features.boundary.title')}</h3>
              <p>{t('features.boundary.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="200">
              <div className="feature-icon">🏘️</div>
              <h3>{t('features.neighbors.title')}</h3>
              <p>{t('features.neighbors.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="250">
              <div className="feature-icon">🔄</div>
              <h3>{t('features.transfer.title')}</h3>
              <p>{t('features.transfer.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="300">
              <div className="feature-icon">👨‍👩‍👧‍👦</div>
              <h3>{t('features.familyAccess.title')}</h3>
              <p>{t('features.familyAccess.description')}</p>
            </div>
            <div className="feature-card" data-aos="fade-up" data-aos-delay="350">
              <div className="feature-icon">💰</div>
              <h3>{t('features.tax.title')}</h3>
              <p>{t('features.tax.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Watchman Spotlight Section */}
      <section className="ai-watchman-spotlight" data-aos="fade-up">
        <div className="container">
          <div className="spotlight-content">
            <div className="spotlight-text">
              <div className="spotlight-badge">
                <span className="badge-pulse"></span>
                🤖 AI-POWERED PROTECTION
              </div>
              <h2 className="gradient-text">{t('aiWatchman.title')}</h2>
              <p className="spotlight-subtitle">{t('aiWatchman.subtitle')}</p>
              <p className="spotlight-tagline">{t('aiWatchman.tagline')}</p>
              
              <div className="spotlight-features">
                <div className="spotlight-feature">
                  <FaCheck className="feature-check" />
                  <span>{t('aiWatchman.feature1')}</span>
                </div>
                <div className="spotlight-feature">
                  <FaCheck className="feature-check" />
                  <span>{t('aiWatchman.feature2')}</span>
                </div>
                <div className="spotlight-feature">
                  <FaCheck className="feature-check" />
                  <span>{t('aiWatchman.feature3')}</span>
                </div>
                <div className="spotlight-feature">
                  <FaCheck className="feature-check" />
                  <span>{t('aiWatchman.feature4')}</span>
                </div>
              </div>

              <a href={getAppStoreURL()} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                {t('aiWatchman.cta')}
              </a>
            </div>

            <div className="spotlight-visual" data-aos="fade-left" data-aos-delay="200">
              <div className="satellite-mockup">
                <div className="satellite-frame">
                  <div className="satellite-header">
                    <div className="status-indicator">
                      <span className="status-dot"></span>
                      Live Monitoring
                    </div>
                    <div className="satellite-date">Last Scan: 2 days ago</div>
                  </div>
                  <div className="satellite-image">
                    <div className="scan-line"></div>
                    <div className="detection-marker marker-1">
                      <div className="marker-pulse"></div>
                      <span className="marker-label">Change Detected</span>
                    </div>
                    <div className="property-boundary"></div>
                  </div>
                  <div className="satellite-footer">
                    <div className="alert-badge">
                      <span>🚨</span> Alert: Boundary Change Detected
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>{t('howItWorks.title')}</h2>
            <p>{t('howItWorks.subtitle')}</p>
          </div>

          <div className="steps-container">
            <div className="step-card" data-aos="fade-up" data-aos-delay="0">
              <div className="step-number">1</div>
              <div className="step-icon">📱</div>
              <h3>{t('howItWorks.step1.title')}</h3>
              <p>{t('howItWorks.step1.description')}</p>
            </div>
            
            <div className="step-arrow" data-aos="fade" data-aos-delay="100">→</div>
            
            <div className="step-card" data-aos="fade-up" data-aos-delay="100">
              <div className="step-number">2</div>
              <div className="step-icon">📍</div>
              <h3>{t('howItWorks.step2.title')}</h3>
              <p>{t('howItWorks.step2.description')}</p>
            </div>
            
            <div className="step-arrow" data-aos="fade" data-aos-delay="200">→</div>
            
            <div className="step-card" data-aos="fade-up" data-aos-delay="200">
              <div className="step-number">3</div>
              <div className="step-icon">🤖</div>
              <h3>{t('howItWorks.step3.title')}</h3>
              <p>{t('howItWorks.step3.description')}</p>
            </div>
            
            <div className="step-arrow" data-aos="fade" data-aos-delay="300">→</div>
            
            <div className="step-card" data-aos="fade-up" data-aos-delay="300">
              <div className="step-number">4</div>
              <div className="step-icon">✅</div>
              <h3>{t('howItWorks.step4.title')}</h3>
              <p>{t('howItWorks.step4.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>{t('testimonials.title')}</h2>
            <p>{t('testimonials.subtitle')}</p>
          </div>

          <div className="testimonials-scroll-container">
            <div className="testimonials-track">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <div key={num} className="testimonial-card">
                  <div className="testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className="star-icon" />
                    ))}
                  </div>
                  <p className="testimonial-text">
                    "{t(`testimonials.testimonial${num}.text`)}"
                  </p>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {t(`testimonials.testimonial${num}.author`).charAt(0)}
                    </div>
                    <div className="author-info">
                      <div className="author-name">
                        {t(`testimonials.testimonial${num}.author`)}
                      </div>
                      <div className="author-role">
                        {t(`testimonials.testimonial${num}.role`)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="scroll-hint">
            <span>← Scroll to see more →</span>
          </div>
        </div>
      </section>

      {/* Video Guide Section */}
      <section className="video-guide" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>{t('videoGuide.title')}</h2>
            <p>{t('videoGuide.subtitle')}</p>
          </div>

          <div className="video-grid">
            <div className="video-card" data-aos="zoom-in" data-aos-delay="0">
              <div className="video-thumbnail">
                <div className="play-button">
                  <FaPlay />
                </div>
                <div className="video-duration">3:45</div>
                <div className="thumbnail-content">
                  <div className="thumbnail-icon">🏡</div>
                  <div className="thumbnail-title">Property Setup</div>
                </div>
              </div>
            </div>

            <div className="video-card" data-aos="zoom-in" data-aos-delay="100">
              <div className="video-thumbnail">
                <div className="play-button">
                  <FaPlay />
                </div>
                <div className="video-duration">5:20</div>
                <div className="thumbnail-content">
                  <div className="thumbnail-icon">🤖</div>
                  <div className="thumbnail-title">AI-Watchman Guide</div>
                </div>
              </div>
            </div>

            <div className="video-card" data-aos="zoom-in" data-aos-delay="200">
              <div className="video-thumbnail">
                <div className="play-button">
                  <FaPlay />
                </div>
                <div className="video-duration">4:10</div>
                <div className="thumbnail-content">
                  <div className="thumbnail-icon">📐</div>
                  <div className="thumbnail-title">Boundary Marking</div>
                </div>
              </div>
            </div>

            <div className="video-card" data-aos="zoom-in" data-aos-delay="300">
              <div className="video-thumbnail">
                <div className="play-button">
                  <FaPlay />
                </div>
                <div className="video-duration">2:55</div>
                <div className="thumbnail-content">
                  <div className="thumbnail-icon">📄</div>
                  <div className="thumbnail-title">Document Upload</div>
                </div>
              </div>
            </div>
          </div>

          <div className="video-categories">
            <p>{t('videoGuide.categories')}</p>
          </div>

          <a href={getAppStoreURL()} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
            {t('videoGuide.cta')}
          </a>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="trust-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>{t('trust.title')}</h2>
            <p>{t('trust.subtitle')}</p>
          </div>

          <div className="trust-grid">
            <div className="trust-card" data-aos="zoom-in" data-aos-delay="0">
              <FaLock className="trust-icon" />
              <h3>{t('trust.feature1')}</h3>
            </div>
            <div className="trust-card" data-aos="zoom-in" data-aos-delay="100">
              <FaShieldAlt className="trust-icon" />
              <h3>{t('trust.feature2')}</h3>
            </div>
            <div className="trust-card" data-aos="zoom-in" data-aos-delay="200">
              <FaClock className="trust-icon" />
              <h3>{t('trust.feature3')}</h3>
            </div>
            <div className="trust-card" data-aos="zoom-in" data-aos-delay="300">
              <FaHeadset className="trust-icon" />
              <h3>{t('trust.feature4')}</h3>
            </div>
          </div>

          {/* <div className="guarantee-badge" data-aos="zoom-in" data-aos-delay="400">
            <div className="guarantee-icon">✓</div>
            <div className="guarantee-text">{t('trust.guarantee')}</div>
          </div> */}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <div className="cta-content">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.subtitle')}</p>
            <div className="cta-buttons">
              <a href={getAppStoreURL()} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                {t('cta.downloadFree')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
